import { Box, Center, Flex, Grid, Image, Spinner, Text, useDisclosure, useToast } from '@chakra-ui/react'
import { Button as UbiButton, Text as UbiText, Column } from '@ubirider/pick-component-library'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'motion/react'

import { NewBillingModal } from './components/BillingProfile/components/NewBillingModal/NewBillingModal'
import { BillingProfile } from './components/BillingProfile'

import { CardsMenu } from './components/CardsMenu'
import { useBilling, useCards, useUser } from '../../context/DataProvider'
import { useEffect } from 'react'
import { addCard } from '../../api/cards'
import { useLocation, useNavigate } from 'react-router-dom'
import { SuccessModal } from '../../modals/SuccessModal'

import EmptyStateIllustration from '../../assets/bill_empty.svg'
import PlusIcon from '../../assets/plus_white.svg'

const MFlex = motion.create(Flex)
const MBox = motion.create(Box)
const MGrid = motion.create(Grid)
const MCenter = motion.create(Center)
const MColumn = motion.create(Column)
const MUbiText = motion.create(UbiText)

const variants = {
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { when: 'beforeChildren', staggerChildren: 0.08, delayChildren: 0.05 },
    },
    exit: { opacity: 0, transition: { duration: 0.15 } },
  },
  section: {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 360, damping: 26 } },
    exit: { opacity: 0, y: 6, transition: { duration: 0.15 } },
  },
  header: {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 360, damping: 28 } },
    exit: { opacity: 0, y: 6, transition: { duration: 0.15 } },
  },
  card: {
    hidden: { opacity: 0, y: 10, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 360, damping: 26 } },
    exit: { opacity: 0, y: 6, scale: 0.98, transition: { duration: 0.15 } },
  },
  buttonLate: {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 360, damping: 26, delay: 0.25 } },
  },
}

export const Wallet = () => {
  const { t } = useTranslation()

  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isOpenSM, onOpen: onOpenSM, onClose: onCloseSM } = useDisclosure()

  const location = useLocation()
  const navigate = useNavigate()

  const { loading: userLoading } = useUser()
  const { loading: cardsLoading } = useCards()
  const { billing, loading: billingLoading } = useBilling()
  const loading = userLoading || cardsLoading || billingLoading

  useEffect(() => {
    const pathname = location.pathname

    const handleAddCard = async () => {
      const params = new URLSearchParams(window.location.search)
      const transactionID = params.get('t')

      try {
        await addCard(transactionID)
        onOpenSM()
      } catch (error) {
        console.error('Error adding new card:', error)
      } finally {
        navigate('/wallet', { replace: true })
      }
    }

    if (pathname === '/wallet/success' || pathname === '/wallet/fail') {
      handleAddCard()
    }
  }, [location.pathname])

  return (
    <MFlex
      direction='column'
      gap={{ sm: '48px', lg: '20px' }}
      flex='1'
      initial='hidden'
      animate='visible'
      exit='exit'
      variants={variants.container}
    >
      <MBox variants={variants.section}>
        <CardsMenu loading={loading} />
      </MBox>

      <MBox flex='1' variants={variants.section}>
        <MFlex
          bg={{ sm: '', md: 'neutral.100', lg: 'neutral.100', xl: 'neutral.100' }}
          rounded='8px'
          padding={{ sm: '', md: '24px', lg: '24px', xl: '24px' }}
          variants={variants.container}
        >
          <MColumn css={{ gap: '16px', flex: '1', w: '100%', maxWidth: '100%' }} variants={variants.container}>
            <MUbiText type='displayXS' weight='bold' variants={variants.header}>
              {t('acqua_vias.billing_info_title')}
            </MUbiText>

            <AnimatePresence mode='wait'>
              {loading ? (
                <MCenter key='loading' minH='240px' variants={variants.card}>
                  <Spinner thickness='6px' color='neutral.500' size='xl' />
                </MCenter>
              ) : billing != null && billing.length > 0 ? (
                <MColumn key='content' css={{ gap: '24px', flex: '1' }} variants={variants.container}>
                  <MUbiText type='textSM' variants={variants.header}>
                    {t('acqua_vias.billing_info_description')}
                  </MUbiText>

                  <MFlex variants={variants.header}>
                    <motion.div variants={variants.buttonLate}>
                      <UbiButton
                        onClick={onOpen}
                        style='ghost'
                        type='secondary'
                        size='small'
                        leftIconProps={{ src: PlusIcon }}
                        label={t('acqua_vias.billing_info_add_button')}
                        css={{ padding: '16px 4px' }}
                      />
                    </motion.div>
                  </MFlex>

                  <MGrid
                    variants={variants.container}
                    templateColumns={{
                      base: '1fr',
                      md: 'repeat(2, 382px)',
                      lg: 'repeat(2, 382px)',
                      xl: 'repeat(2, 382px)',
                      xxl: 'repeat(2, 382px)',
                    }}
                    gap={{ base: '24px', md: '16px' }}
                    w='100%'
                  >
                    {billing.map((data, id) => (
                      <motion.div key={data?.id ?? id} variants={variants.card} style={{ width: '100%' }}>
                        <BillingProfile id={id} info={data} />
                      </motion.div>
                    ))}
                  </MGrid>
                </MColumn>
              ) : (
                <MFlex
                  key='empty'
                  direction='column'
                  my={{ sm: '', md: '48px', lg: '48px', xl: '48px' }}
                  mx={{ sm: '', md: '96px', lg: '96px', xl: '96px' }}
                  justifyContent='center'
                  alignItems='center'
                  textAlign='center'
                  gap='24px'
                  variants={variants.card}
                >
                  <motion.div variants={variants.header}>
                    <Image src={EmptyStateIllustration} height='160px' />
                  </motion.div>

                  <MUbiText type='textSM' css={{ textAlign: 'center' }} variants={variants.header}>
                    {t('acqua_vias.billing_info_description')}
                  </MUbiText>

                  <Center>
                    <motion.div variants={variants.buttonLate}>
                      <Flex display={{ sm: 'none', lg: 'flex' }}>
                        <UbiButton
                          onClick={onOpen}
                          style='outline'
                          type='secondary'
                          leftIconProps={{ src: PlusIcon }}
                          label={t('acqua_vias.billing_info_add_button')}
                        />
                      </Flex>
                      <Flex display={{ sm: 'flex', lg: 'none' }}>
                        <UbiButton
                          onClick={onOpen}
                          style='outline'
                          size='small'
                          type='secondary'
                          leftIconProps={{ src: PlusIcon }}
                          label={t('acqua_vias.billing_info_add_button')}
                        />
                      </Flex>
                    </motion.div>
                  </Center>
                </MFlex>
              )}
            </AnimatePresence>
          </MColumn>

          <NewBillingModal onOpen={onOpen} onClose={onClose} isOpen={isOpen} />
          <SuccessModal
            title={'Cartão adicionado'}
            description={'O cartão pode levar alguns segundos para aparecer na lista.'}
            onOpen={onOpenSM}
            onClose={onCloseSM}
            isOpen={isOpenSM}
          />
        </MFlex>
      </MBox>
    </MFlex>
  )
}
